/**
 * Bus de eventos tipado (singleton) — única vía de comunicación entre las
 * escenas de Phaser y los módulos de lógica pura. Ver
 * docs/plans/2026-07-13-plan-implementacion.md sección "Bus de eventos".
 *
 * Lógica pura sin Phaser: este archivo no importa Phaser, solo implementa un
 * emisor de eventos genérico y el mapa de payloads del juego.
 */

import type { CardOffer } from './types';

// ---------------------------------------------------------------------------
// Mapa de eventos: nombre del evento -> forma de su payload
// ---------------------------------------------------------------------------

export interface GameEventMap {
  'combat:waveStart': { wave: number; total: number };
  'combat:enemyKilled': { type: string; x: number; balls: number };
  'combat:waveCleared': { wave: number };
  'combat:heroDamaged': { hp: number; maxHp: number };
  'combat:heroDied': Record<string, never>;
  'combat:bossSpawned': { name: string; hp: number };

  'pour:ready': { balls: number; boardId: string };
  'pour:done': { ballsGained: number };

  'shop:open': { offers: CardOffer[] };
  'shop:purchased': { cardId: string };
  'shop:closed': Record<string, never>;

  'run:won': { rewards: { coins: number; xp: number; gearDrops: number } };
  'run:lost': { partialRewards: { coins: number; xp: number } };
  'run:revived': Record<string, never>;

  'meta:changed': Record<string, never>;
  'ui:speedChanged': { speed: 1 | 2 };
}

export type GameEventName = keyof GameEventMap;

type Listener<E extends GameEventName> = (payload: GameEventMap[E]) => void;

/**
 * Emisor de eventos tipado minimalista, sin dependencias externas.
 * No usamos Phaser.Events.EventEmitter aquí para mantener core/ libre de
 * Phaser; las escenas pueden envolver esto o usar su propio emitter y
 * reenviar eventos si les conviene.
 */
export class TypedEventBus {
  private listeners = new Map<GameEventName, Set<Listener<GameEventName>>>();

  on<E extends GameEventName>(event: E, listener: Listener<E>): () => void {
    const set = this.listeners.get(event) ?? new Set();
    set.add(listener as Listener<GameEventName>);
    this.listeners.set(event, set);
    return () => this.off(event, listener);
  }

  once<E extends GameEventName>(event: E, listener: Listener<E>): () => void {
    const off = this.on(event, (payload) => {
      off();
      listener(payload);
    });
    return off;
  }

  off<E extends GameEventName>(event: E, listener: Listener<E>): void {
    this.listeners.get(event)?.delete(listener as Listener<GameEventName>);
  }

  emit<E extends GameEventName>(event: E, payload: GameEventMap[E]): void {
    const set = this.listeners.get(event);
    if (!set) return;
    // copia defensiva: permite que un listener se desuscriba durante el emit
    for (const listener of [...set]) {
      listener(payload);
    }
  }

  clear(event?: GameEventName): void {
    if (event) {
      this.listeners.delete(event);
    } else {
      this.listeners.clear();
    }
  }
}

/** Instancia singleton compartida por toda la app. */
export const gameEvents = new TypedEventBus();
